import type { StrategyNode } from '../types/BaseNode';
import { toSnakeCaseKeys } from './case';
import { SingleNode } from '../types/SingleNode';
import { CompositeNode } from '../types/CompositeNode';
import { FormatterNode } from '../types/FormatterNode';
import { PnLNode } from '../types/PnLNode';
import { VarianceNode } from '../types/VarianceNode';
import { MeepMeepNode } from '../types/MeepMeepNode';
import { RegressionNode } from '../types/RegressionNode';
import { setNodeFactory } from './nodeFactory';

function pickFirst<T = any>(...values: T[]): T | undefined {
  for (const value of values) {
    if (value !== undefined) return value;
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function unwrapLegacyNestedConfig(config: Record<string, any> = {}) {
  let cursor: Record<string, any> = { ...config };
  let extractedTypeId = pickFirst<string>(cursor.typeId, cursor.type_id);

  while (isRecord(cursor.props)) {
    const nested = cursor.props;
    const nestedTypeId = pickFirst<string>(nested.typeId, nested.type_id);
    if (!extractedTypeId && nestedTypeId) {
      extractedTypeId = nestedTypeId;
    }
    cursor = nested;
  }

  return {
    typeId: extractedTypeId ?? '',
    payload: cursor,
  };
}

function omitKeys(obj: Record<string, any>, keys: string[]): Record<string, any> {
  const next = { ...obj };
  for (const key of keys) {
    delete next[key];
  }
  return next;
}

function normalizeManualRename(data: any): boolean | undefined {
  return pickFirst<boolean | undefined>(data?.isManualRename, data?.is_manual_rename);
}

function normalizeSingleConfig(config: Record<string, any> = {}) {
  const unwrapped = unwrapLegacyNestedConfig(config);
  const typeId = unwrapped.typeId;
  const props = omitKeys(unwrapped.payload, ['typeId', 'type_id', 'start_date', 'end_date']);
  return { typeId, props };
}

function normalizeFormatterConfig(config: Record<string, any> = {}) {
  const child = pickFirst<any>(config.child, null);
  const unwrapped = unwrapLegacyNestedConfig(omitKeys(config, ['child']));
  const typeId = unwrapped.typeId;
  const props = omitKeys(unwrapped.payload, ['typeId', 'type_id', 'start_date', 'end_date']);
  return { typeId, child, props };
}


function compact<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null)
  ) as Partial<T>;
}

function normalizeFormatterProps(typeId: string | undefined, props: Record<string, any> | undefined): Record<string, any> {
  const nextProps = { ...(props ?? {}) };

  if (typeId === 'moving_average_slope' && nextProps.min_slope_pct == null && nextProps.min_slope_delta != null) {
    nextProps.min_slope_pct = nextProps.min_slope_delta;
    delete nextProps.min_slope_delta;
  }

  return nextProps;
}

export function serializeNode(node: StrategyNode, start_date: string | null, end_date: string | null): any {
  if (node.type === 'Single') {
    const cfg = node.config as { typeId: string; props: Record<string, any> };

    const configFlat = compact({
      typeId: cfg?.typeId ?? '',
      ...(cfg?.props ?? {}),
      start_date,
      end_date,
    });

    return toSnakeCaseKeys({
      id: node.id,
      title: node.title,
      isManualRename: node.isManualRename,
      type: 'Single',
      config: configFlat,
    });
  }

  if (node.type === 'Composite') {
    const left = node.config.left ? serializeNode(node.config.left, start_date, end_date) : undefined;
    const right = node.config.right ? serializeNode(node.config.right, start_date, end_date) : undefined;

    return toSnakeCaseKeys({
      id: node.id,
      title: node.title,
      isManualRename: node.isManualRename,
      type: 'Composite',
      config: compact({
        ...(node.config.operator && { operator: node.config.operator }),
        ...(node.config.combine_on && { combine_on: node.config.combine_on }),
        left,
        right,
      }),
    });
  }

  if (node.type === 'Formatter') {
    const cfg = node.config as { typeId: string; props: Record<string, any>; child?: StrategyNode };
    const normalizedProps = normalizeFormatterProps(cfg?.typeId, cfg?.props);

    const configFlat = compact({
      typeId: cfg?.typeId ?? '',
      ...normalizedProps,
      child: cfg.child ? serializeNode(cfg.child, start_date, end_date) : null,
      start_date,
      end_date,
    });

    return toSnakeCaseKeys({
      id: node.id,
      title: node.title,
      isManualRename: node.isManualRename,
      type: 'Formatter',
      config: configFlat,
    });
  }

  if (node.type === 'PnL') {
    const pnl = node as PnLNode;
    const entry = pnl.config.entry ? serializeNode(pnl.config.entry, start_date, end_date) : undefined;
    let exit = pnl.config.exit ? serializeNode(pnl.config.exit, start_date, end_date) : undefined;

    if (pnl.config.exitMode === 'hold' && pnl.config.exit) {
      // Use standard Formatter structure for offset
      const offsetNode = new FormatterNode(`${pnl.id}_hold_exit`, 'Hold Offset');
      offsetNode.config = {
        typeId: 'offset',
        props: {
          value: String(pnl.config.holdDuration || 1),
          type: pnl.config.holdType || 'hour'
        },
        child: pnl.config.exit
      };
      
      // Manually serialize this virtual formatter node
      exit = {
        id: offsetNode.id,
        type: 'Formatter',
        title: offsetNode.title,
        config: toSnakeCaseKeys({
          type_id: 'offset',
          value: String(pnl.config.holdDuration || 1),
          type: pnl.config.holdType || 'hour',
          child: serializeNode(pnl.config.exit, start_date, end_date),
          start_date,
          end_date
        })
      };
    }

    return toSnakeCaseKeys({
      id: node.id,
      title: node.title,
      isManualRename: node.isManualRename,
      type: 'PnL',
      config: compact({
        entry,
        exit,
        buy: entry,  // Required for some backends
        sell: exit,  // Required for some backends
        max_lots: pnl.config.maxLots,
        lot_size: pnl.config.lotSize,
        fees: pnl.config.fees,
        mode: pnl.config.mode,
        exit_mode: pnl.config.exitMode,
        hold_duration: pnl.config.holdDuration,
        hold_type: pnl.config.holdType,
        ...(pnl.config.exitMode === 'hold' && { combine_on: 'next_match' })
      }),
    });
  }

  if (node.type === 'Variance') {
    const target = node.config.target ? serializeNode(node.config.target, start_date, end_date) : undefined;
    const parameters = (node.config.parameters ?? []).map((p: StrategyNode) =>
      serializeNode(p, start_date, end_date)
    );

    return toSnakeCaseKeys({
      id: node.id,
      title: node.title,
      isManualRename: node.isManualRename,
      type: 'Variance',
      config: { target, parameters },
    });
  }

  if (node.type === 'MeepMeep') {
    const meepMeep = node as MeepMeepNode;
    const main_market = meepMeep.config.main_market ? serializeNode(meepMeep.config.main_market, start_date, end_date) : undefined;
    const signal = meepMeep.config.signal ? serializeNode(meepMeep.config.signal, start_date, end_date) : undefined;

    return toSnakeCaseKeys({
      id: node.id,
      title: node.title,
      isManualRename: node.isManualRename,
      type: 'MeepMeep',
      isMicrostructure: true,
      isPayloadAware: true,
        config: compact({
            main_market,
            signal,
            latency_ms: meepMeep.config.latency_ms,
            insample_days: meepMeep.config.insample_days,
            target_forward_ticks: meepMeep.config.target_forward_ticks,
            max_volume: meepMeep.config.max_volume,
            trade_margin: meepMeep.config.trade_margin,
            trade_margin_regime: meepMeep.config.trade_margin_regime,
        }),
    });
  }

  if (node.type === 'Regression') {
    const reg = node as RegressionNode;
    const inputs = (reg.config.inputs || []).map(input => serializeNode(input, start_date, end_date));

    return toSnakeCaseKeys({
      id: node.id,
      title: node.title,
      isManualRename: node.isManualRename,
      type: 'Regression',
      isMicrostructure: true,
      isPayloadAware: true,
      config: compact({
        model_type: reg.config.model_type,
        weights: reg.config.weights,
        inputs,
        last_calibrated_at: reg.config.last_calibrated_at,
        calibration_artifact_id: reg.config.calibration_artifact_id,
        calibration_artifact_path: reg.config.calibration_artifact_path,
        feature_tree_hash: reg.config.feature_tree_hash,
        feature_labels: reg.config.feature_labels,
      }),
    });
  }

  throw new Error(`Unknown node type: ${node}`);
}


export function deserializeNode(data: any): StrategyNode {
  const { id, title, type } = data;
  const config = data?.config ?? {};
  const isManualRename = normalizeManualRename(data);

  if (type === 'Single') {
    const normalized = normalizeSingleConfig(config);
    return new SingleNode(id, title, isManualRename, {
      typeId: normalized.typeId,
      props: normalized.props
    });
  }

  if (type === 'Composite') {
    const node = new CompositeNode(id, title, config.operator, config.combine_on, undefined, undefined, isManualRename);
    node.config.left = config.left ? deserializeNode(config.left) : undefined;
    node.config.right = config.right ? deserializeNode(config.right) : undefined;
    node.config.window = config.window;
    return node;
  }

  if (type === 'Formatter') {
    const normalized = normalizeFormatterConfig(config);
    const node = new FormatterNode(id, title, isManualRename);
    node.config = {
      typeId: normalized.typeId,
      props: normalizeFormatterProps(normalized.typeId, normalized.props),
      child: normalized.child ? deserializeNode(normalized.child) : null
    };
    return node;
  }

  if (type === 'PnL') {
    const node = new PnLNode(
      id,
      title,
      undefined,
      undefined,
      pickFirst<number>(config.maxLots, config.max_lots),
      pickFirst<number>(config.lotSize, config.lot_size),
      config.fees,
      config.mode || 'long',
      isManualRename,
      pickFirst<'signal' | 'hold'>(config.exitMode, config.exit_mode) || 'signal',
      pickFirst<number>(config.holdDuration, config.hold_duration) || 1,
      pickFirst<'minute' | 'hour' | 'day'>(config.holdType, config.hold_type) || 'hour'
    );
    node.config.entry = (config.entry ? deserializeNode(config.entry) : undefined) || (config.buy ? deserializeNode(config.buy) : undefined);
    node.config.exit = (config.exit ? deserializeNode(config.exit) : undefined) || (config.sell ? deserializeNode(config.sell) : undefined);
    return node;
  }

  if (type === 'Variance') {
    const target = config.target ? deserializeNode(config.target) : undefined;
    const parameters = Array.isArray(config.parameters)
      ? config.parameters.map((p: any) => deserializeNode(p))
      : [];
    const node = new VarianceNode(id, title, target, parameters, isManualRename);
    return node;
  }

  if (type === 'MeepMeep') {
    const rawSignal = pickFirst<any>(
      config.signal,
      Array.isArray(config.signals) ? config.signals[0] : undefined,
    );
    const rawMainMarket = pickFirst<any>(
      config.main_market,
      config.mainMarket,
      config.execution_market,
      config.executionMarket,
    );
    const signal = rawSignal ? deserializeNode(rawSignal) : undefined;
    const main_market = rawMainMarket ? deserializeNode(rawMainMarket) : undefined;
    const rawLatencyMs = pickFirst<number>(config.latency_ms, config.latencyMs);
    const rawLatencySeconds = pickFirst<number>(config.latency_seconds, config.latencySeconds);
    const latencyMs = rawLatencyMs ?? (
      rawLatencySeconds !== undefined
        ? (rawLatencySeconds <= 1 ? rawLatencySeconds * 1000 : rawLatencySeconds)
        : 35
    );
    const node = new MeepMeepNode(
      id,
      title,
      main_market,
      signal,
      latencyMs,
      pickFirst<number>(config.insample_days, config.insampleDays) ?? 3,
      pickFirst<number>(config.target_forward_ticks, config.targetForwardTicks) ?? 25,
      pickFirst<number>(config.max_volume, config.maxVolume, config.lot_size, config.lotSize) ?? 1,
      pickFirst<number>(config.trade_margin, config.tradeMargin, config.threshold) ?? 0,
      pickFirst<string>(config.trade_margin_regime, config.tradeMarginRegime) ?? 'static',
      isManualRename
    );
    return node;
  }

  if (type === 'Regression') {
    const inputs = (config.inputs || []).map((input: any) => deserializeNode(input));
    const node = new RegressionNode(
      id,
      title,
      pickFirst<any>(config.model_type, config.modelType),
      config.weights,
      inputs,
      isManualRename,
      pickFirst<string>(config.last_calibrated_at, config.lastCalibratedAt),
      pickFirst<string>(config.calibration_artifact_id, config.calibrationArtifactId),
      pickFirst<string>(config.calibration_artifact_path, config.calibrationArtifactPath),
      pickFirst<string>(config.feature_tree_hash, config.featureTreeHash),
      pickFirst<string[]>(config.feature_labels, config.featureLabels)
    );
    return node;
  }

  throw new Error('Unknown node type: ' + JSON.stringify(data));
}

// Register the factory so node constructors can re-instantiate raw children
setNodeFactory(deserializeNode);
